import React, { useState, useRef, useEffect } from 'react';
import WaveSurfer from 'wavesurfer.js';
import {
  C,
  CARD_W,
  COUNTDOWN_CARD_H,
  COUNTDOWN_CARD_GAP,
  nx,
  ny,
  normalizeAudioField,
} from '../flowCanvas.constants';

// Card zone heights — must sum to COUNTDOWN_CARD_H (150)
const HEADER_H = 28;  // label + controls band
const BODY_H   = 72;  // content area: copy text box + info
const AUDIO_H  = 50;  // waveform row at bottom (includes bottom padding)

const COPY_W = 52;
const PAD    = 10;

// ─── Mini waveform audio player ───────────────────────────────────────────────
function CountdownCardAudio({ src, audioName, volume, disabled, x, y, w, waveId, onToggleDisable }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const wsRef = useRef(null);

  const BTN_R  = 11;
  const WAVE_H = 18;
  const VOL_W  = 26;
  const MUTE_R = 9;
  const cy     = y + BTN_R;
  const waveX  = x + BTN_R * 2 + 4;
  const waveW  = w - BTN_R * 2 - 4 - VOL_W - 4 - MUTE_R * 2 - 8;
  const volX   = waveX + waveW + 4;
  const muteX  = volX + VOL_W + 4;

  useEffect(() => {
    if (!src || disabled) {
      if (wsRef.current) { wsRef.current.destroy(); wsRef.current = null; }
      return;
    }
    const t = setTimeout(() => {
      const el = document.getElementById(waveId);
      if (!el) return;
      const ws = WaveSurfer.create({
        container: el, waveColor: '#BCA4FF', progressColor: '#9146FF',
        url: src, height: WAVE_H, normalize: true, hideScrollbar: true,
      });
      wsRef.current = ws;
      ws.on('play',   () => setIsPlaying(true));
      ws.on('finish', () => setIsPlaying(false));
      ws.on('pause',  () => setIsPlaying(false));
      ws.setVolume(Math.max(0, Math.min(1, (typeof volume === 'number' ? volume : 100) / 100)));
    }, 60);
    return () => {
      clearTimeout(t);
      if (wsRef.current) { wsRef.current.destroy(); wsRef.current = null; }
    };
  }, [src, waveId, volume, disabled]);

  const toggle = (e) => {
    e.stopPropagation();
    if (!wsRef.current || disabled) return;
    wsRef.current.isPlaying() ? wsRef.current.stop() : wsRef.current.play();
  };

  if (!audioName && !src) {
    return (
      <text x={x + w / 2} y={cy + 1}
        textAnchor="middle" dominantBaseline="middle"
        fontSize="8" fill={C.textDim}
        style={{ pointerEvents: 'none', userSelect: 'none' }}>No audio</text>
    );
  }

  const alpha  = disabled ? 0.35 : 1;
  const volPct = typeof volume === 'number' ? volume : 100;

  return (
    <>
      {/* Play / stop */}
      <g onClick={toggle} style={{ cursor: disabled ? 'default' : 'pointer' }} opacity={alpha}>
        <circle cx={x + BTN_R} cy={cy} r={BTN_R}
          fill={isPlaying ? C.purple : C.circle} stroke={C.purple} strokeWidth="1.5" />
        <text x={x + BTN_R} y={cy + 1} textAnchor="middle" dominantBaseline="middle"
          fontSize="9" fill={isPlaying ? '#fff' : C.purple}
          style={{ pointerEvents: 'none', userSelect: 'none' }}>
          {isPlaying ? '■' : '▶'}
        </text>
      </g>

      {/* Waveform or filename fallback */}
      {src && !disabled ? (
        <foreignObject x={waveX} y={cy - WAVE_H / 2} width={waveW} height={WAVE_H}>
          <div xmlns="http://www.w3.org/1999/xhtml"
            style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}>
            <div id={waveId} style={{ width: '100%', height: `${WAVE_H}px` }} />
          </div>
        </foreignObject>
      ) : (
        <text x={waveX} y={cy + 1}
          fontSize="7.5" fill={C.textMuted} dominantBaseline="middle" opacity={alpha}
          style={{ pointerEvents: 'none', userSelect: 'none' }}>
          {(audioName || '').length > 18 ? (audioName || '').slice(0, 18) + '…' : (audioName || '')}
        </text>
      )}

      {/* Volume badge */}
      <rect x={volX} y={cy - 7} width={VOL_W} height={14} rx="7"
        fill="rgba(0,0,0,0.65)" opacity={alpha} />
      <text x={volX + VOL_W / 2} y={cy + 1}
        textAnchor="middle" dominantBaseline="middle"
        fontSize="7" fontWeight="700" fill={C.greenText} opacity={alpha}
        style={{ pointerEvents: 'none', userSelect: 'none' }}>
        {volPct}%
      </text>

      {/* Mute / unmute toggle */}
      {onToggleDisable && (
        <g onClick={e => { e.stopPropagation(); onToggleDisable(); }} style={{ cursor: 'pointer' }}>
          <circle cx={muteX + MUTE_R} cy={cy} r={MUTE_R}
            fill={disabled ? 'rgba(145,70,255,0.2)' : 'rgba(220,38,38,0.15)'}
            stroke={disabled ? C.purple : '#dc2626'} strokeWidth="1" />
          <text x={muteX + MUTE_R} y={cy + 1}
            textAnchor="middle" dominantBaseline="middle"
            fontSize="10" fill={disabled ? C.purple : '#dc2626'}
            style={{ pointerEvents: 'none', userSelect: 'none' }}>
            {disabled ? '♪' : '×'}
          </text>
        </g>
      )}
    </>
  );
}

// ─── Data helper ──────────────────────────────────────────────────────────────
function getTextCards(step, liveCustomisationData) {
  const cfg     = step?.customisationConfig;
  const tabKey  = cfg?.tabKey;
  const varKey  = cfg?.varKey;
  const varName = cfg?.variables?.[0]?.variableName;
  const live    = tabKey && varKey && varName
    ? liveCustomisationData?.[tabKey]?.[varKey]?.[varName]
    : null;
  const vals = Array.isArray(live) ? live : (step?.populatedData?.variables?.[0]?.allValues || []);
  return vals.length > 0 ? vals : [{}];
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function CountdownTextStackNode({
  step, selected, selectedIndex, onClick, onReorder, onRemove, onCardFieldChange,
  centreY, overrideX, overrideY, liveCustomisationData,
}) {
  const x      = overrideX ?? nx(step);
  const y      = overrideY ?? ny(step, centreY);
  const cards  = getTextCards(step, liveCustomisationData);
  const count  = cards.length;
  const totalH = count * (COUNTDOWN_CARD_H + COUNTDOWN_CARD_GAP) - COUNTDOWN_CARD_GAP;
  const left   = x - CARD_W / 2;
  const top    = y - totalH / 2;

  return (
    <g className="fc-node fc-node--countdown-text-stack" style={{ cursor: 'pointer' }}>
      {cards.map((card, i) => {
        const cardTop      = top + i * (COUNTDOWN_CARD_H + COUNTDOWN_CARD_GAP);
        const sfxField     = normalizeAudioField(card?.audioFilename);
        const copyText     = card?.copy ?? `${count - i}`;
        const fontColour   = card?.fontColour ?? C.text;
        const isSel        = selectedIndex === i;
        const canUp        = i > 0;
        const canDown      = i < count - 1;
        const audioDisabled = card?.playSFX === false;

        const waveId  = `cd-txt-wave-${step?.stepId ?? (x | 0)}-${i}`;

        // Position label counts down to "Final Count"
        const posLabel = i === count - 1 ? 'Final Count' : `Count ${count - 1 - i}`;

        // Zone y references (absolute)
        const bodyTop    = cardTop + HEADER_H;
        const copyBoxTop = bodyTop + (BODY_H - COPY_W) / 2;
        const audioTop   = bodyTop + BODY_H;

        // Header control positions
        const hdrCY   = cardTop + HEADER_H / 2;
        const upCX    = left + CARD_W - 56;
        const downCX  = left + CARD_W - 37;
        const sepX    = left + CARD_W - 26;
        const trashCX = left + CARD_W - 13;

        return (
          <g key={i} onClick={() => onClick?.(i)} style={{ cursor: 'pointer' }}>
            {/* Card shadow */}
            <rect x={left + 2} y={cardTop + 4} width={CARD_W} height={COUNTDOWN_CARD_H}
              rx="8" fill="#000" fillOpacity="0.25" />

            {/* Card background */}
            <rect x={left} y={cardTop} width={CARD_W} height={COUNTDOWN_CARD_H}
              rx="8"
              fill={isSel ? C.cardSel : C.card}
              stroke={isSel ? C.sel : C.border}
              strokeWidth={isSel ? 2 : 1} />

            {/* Body divider */}
            <line x1={left} y1={cardTop + HEADER_H} x2={left + CARD_W} y2={cardTop + HEADER_H}
              stroke={C.border} strokeWidth="0.75" />

            {/* Position label — left-aligned plain text */}
            <text
              x={left + PAD} y={hdrCY + 1}
              dominantBaseline="middle"
              fontSize="8.5" fontWeight="700" fill={isSel ? C.sel : C.textMuted}
              style={{ pointerEvents: 'none', userSelect: 'none' }}>
              {posLabel}
            </text>

            {/* ▲ Up */}
            <g onClick={e => { e.stopPropagation(); if (canUp) onReorder?.(i, i - 1); }}
              style={{ cursor: canUp ? 'pointer' : 'default' }}>
              <rect x={upCX - 10} y={cardTop + 4} width={20} height={HEADER_H - 8} fill="transparent" />
              <polygon
                points={`${upCX},${hdrCY - 5} ${upCX - 5},${hdrCY + 3} ${upCX + 5},${hdrCY + 3}`}
                fill={canUp ? C.textMuted : C.textDim} />
            </g>

            {/* ▼ Down */}
            <g onClick={e => { e.stopPropagation(); if (canDown) onReorder?.(i, i + 1); }}
              style={{ cursor: canDown ? 'pointer' : 'default' }}>
              <rect x={downCX - 10} y={cardTop + 4} width={20} height={HEADER_H - 8} fill="transparent" />
              <polygon
                points={`${downCX},${hdrCY + 5} ${downCX - 5},${hdrCY - 3} ${downCX + 5},${hdrCY - 3}`}
                fill={canDown ? C.textMuted : C.textDim} />
            </g>

            {/* Separator between arrows and trash */}
            <line x1={sepX} y1={cardTop + 6} x2={sepX} y2={cardTop + HEADER_H - 6}
              stroke={C.border} strokeWidth="0.75" />

            {/* Trash button */}
            {onRemove && (
              <g onClick={e => { e.stopPropagation(); onRemove(i); }} style={{ cursor: 'pointer' }}>
                <circle cx={trashCX} cy={hdrCY} r={9} fill="#dc2626" />
                <g transform={`translate(${trashCX}, ${hdrCY})`}
                  style={{ pointerEvents: 'none', userSelect: 'none' }}>
                  <rect x="-2" y="-5" width="4" height="2" rx="1" fill="#fee2e2" />
                  <rect x="-4.5" y="-3" width="9" height="1.5" rx="0.75" fill="#fee2e2" />
                  <rect x="-3.5" y="-1" width="7" height="5.5" rx="0.8" fill="#fee2e2" />
                  <rect x="-1.8" y="0.5" width="1" height="3" rx="0.5" fill="#dc2626" />
                  <rect x="0.8" y="0.5" width="1" height="3" rx="0.5" fill="#dc2626" />
                </g>
              </g>
            )}

            {/* Copy text box */}
            <rect x={left + PAD} y={copyBoxTop} width={COPY_W} height={COPY_W}
              rx="4"
              fill="rgba(145,70,255,0.08)"
              stroke="rgba(145,70,255,0.22)"
              strokeWidth="1" />
            <text
              x={left + PAD + COPY_W / 2}
              y={copyBoxTop + COPY_W / 2}
              textAnchor="middle" dominantBaseline="middle"
              fontSize={Math.min(26, 52 / Math.max(copyText.length, 1))}
              fontWeight="800"
              fill={fontColour}
              style={{ pointerEvents: 'none', userSelect: 'none' }}>
              {copyText}
            </text>

            {/* Step name */}
            <text x={left + PAD + COPY_W + 10} y={bodyTop + 20}
              fontSize="10" fontWeight="700" fill={C.text}
              style={{ pointerEvents: 'none', userSelect: 'none' }}>
              {card?.countDownName ?? `Step ${i + 1}`}
            </text>

            {/* Font size */}
            <text x={left + PAD + COPY_W + 10} y={bodyTop + 38}
              fontSize="8" fill={C.textMuted}
              style={{ pointerEvents: 'none', userSelect: 'none' }}>
              {card?.fontSize != null ? `${card.fontSize}px` : 'Default size'}
            </text>

            {/* Audio section divider */}
            <line x1={left + PAD} y1={audioTop} x2={left + CARD_W - PAD} y2={audioTop}
              stroke={C.border} strokeWidth="0.75" />

            {/* Audio row */}
            <CountdownCardAudio
              src={sfxField.srcUrl || null}
              audioName={sfxField.name || null}
              volume={card?.volume ?? 100}
              disabled={audioDisabled}
              x={left + PAD}
              y={audioTop + (AUDIO_H - 22) / 2}
              w={CARD_W - PAD * 2}
              waveId={waveId}
              onToggleDisable={onCardFieldChange
                ? () => onCardFieldChange(i, 'playSFX', audioDisabled)
                : null}
            />
          </g>
        );
      })}
    </g>
  );
}
